export const M10_UNIT_6 = {
  id: "m10-u6-first-times",
  title: "Unit 6: First Times",
  topic: "Never / Ever + Present & Past Perfect + Travel & Buildings",
  iconType: "compass",
  grammarImage: "/m10-u6-first-times.jpg",
  grammarText: "Unit focus: Using 'ever' and 'never' with Present Perfect and Past Perfect to talk about first times, unique experiences, and superlative moments in travel and life.",

  sets: [

    // ===================================================================
    // SET 1: BUILDINGS VOCABULARY — POSITIVE ADJECTIVES (B1+)
    // ===================================================================
    {
      id: "m10-u6-set1",
      title: "Set 1: Describing Buildings",
      context: `*Lagos, Monday morning — Amara's architecture blog*

Amara is an architect who reviews buildings around the world.
Today she writes about the places that have stayed with her.
"Some buildings you forget immediately. Others, you never do."

*Lagos, lunes por la mañana — Blog de arquitectura de Amara*

Amara es una arquitecta que reseña edificios de todo el mundo.
Hoy escribe sobre los lugares que se le han quedado grabados.
"Algunos edificios los olvidas de inmediato. A otros, nunca."`,

      questions: [
        {
          type: "match",
          q: "Match the building adjectives:",
          pairs: [
            { left: "spacious", right: "espacioso" },
            { left: "charming", right: "encantador" },
            { left: "comfortable", right: "cómodo" }
          ],
          a: "completed",
          successTip: "Spacious = lots of space. Charming = delightfully attractive. Comfortable = cosy and relaxing. The positive trio for building reviews! 🏛️"
        },

        {
          type: "mc",
          q: "Amara writes: 'The apartment was incredibly ___ — three large rooms and high ceilings.'",
          options: ["spacious", "derelict", "deserted"],
          a: "spacious",
          tip: "Spacious = espacioso (large and open).",
          successTip: "spacious = amplio, derelict = en ruinas / descuidado, deserted = desierto / vacío / abandonado"
        },

        {
          type: "mc",
          q: "Her favourite café was in a ___ old building with ivy on the walls.",
          options: ["charming", "high-rise", "open-plan"],
          a: "charming",
          tip: "Charming = encantador (attractively pleasant).",
          successTip: "Charming is usually for small, old, or quaint places. 'A charming little café, a charming village street.' Not used for modern glass towers! 🌿"
        },

        {
          type: "match",
          q: "Match more adjectives and travel expressions:",
          pairs: [
            { left: "popular", right: "popular/concurrido" },
            { left: "off the beaten track", right: "fuera de lo común / poco turístico" },
            { left: "open-plan", right: "de planta abierta (sin divisiones)" }
          ],
          a: "completed",
          successTip: "'Off the beaten track' = fuera de las rutas habituales. 'We found a café off the beaten track — no tourists, just locals.' You'll use this soon! 🗺️"
        },

        {
          type: "mc",
          q: "The city centre hotel was too ___ for Amara — she preferred quieter spots.",
          options: ["popular", "spacious", "comfortable"],
          a: "popular",
          tip: "Popular = muy concurrido (not always positive in context)."
        },

        {
          type: "mc",
          q: "The apartment had an ___ kitchen and living room — no walls dividing them.",
          options: ["open-plan", "ancient", "derelict"],
          a: "open-plan",
          tip: "Open-plan = de planta abierta (no internal dividing walls).",
          successTip: "Open-plan is very trendy in modern architecture! The opposite is 'compartmentalised'. Very common in property listings and design magazines! 🏠"
        },

        {
          type: "input",
          q: "Amara's review: 'The hotel bed was the most ___ I had ever slept in.'",
          a: "comfortable",
          alts: ["Comfortable","luxurious", "soft", "firm", "supportive"],
          tip: "Comfortable = cómodo (pleasant and relaxing)."
        },

        {
          type: "mc",
          q: "Have you ever stayed somewhere truly ___  — far from tourists and tourist traps?",
          options: ["off the beaten track", "open-plan", "high-rise"],
          a: "off the beaten track",
          tip: "Off the beaten track = away from the usual tourist routes.",
          successTip: "You used it! 'Off the beaten track' = fuera del camino trillado. 'Beaten track' = camino trillado. Perfect for travel writing! ✍️"
        },

        {
          type: "mc",
          q: "Amara's best review: 'I have never felt so at home in such a ___ , welcoming space.'",
          options: ["comfortable", "derelict", "deserted"],
          a: "comfortable",
          tip: "Comfortable = warm, easy to relax in (best fit here)."
        },

        {
          type: "input",
          q: "Amara reflects: 'I have visited hundreds of buildings, but I have ___ forgotten the first truly spacious home I designed.'",
          a: "never",
          alts: ["Never"],
          successTip: "I have never forgotten = nunca he olvidado. Present Perfect + never = experience up to now. Your first 'never' structure! ✅"
        }
      ]
    },

    // ===================================================================
    // SET 2: BUILDINGS — STRIKING ADJECTIVES (B1+)
    // ===================================================================
    {
      id: "m10-u6-set2",
      title: "Set 2: Unusual Buildings",
      context: `*Copenhagen, Tuesday — Soren's travel blog*

Soren travels Europe looking for unusual buildings to write about.
"I've found a hidden gem in Porto — an abandoned factory turned art space."
"It was derelict for thirty years. Now it's the most beautiful building I've ever seen."

*Copenhague, martes — Blog de viajes de Soren*

Soren viaja por Europa buscando edificios inusuales sobre los que escribir.
"He encontrado una joya escondida en Oporto — una fábrica abandonada convertida en espacio de arte."
"Estuvo en ruinas durante treinta años. Ahora es el edificio más hermoso que he visto."`,

      questions: [
        {
          type: "match",
          q: "Match the striking building adjectives:",
          pairs: [
            { left: "ancient", right: "antiguo/milenario" },
            { left: "derelict", right: "abandonado/en ruinas" },
            { left: "deserted", right: "desierto/vacío de personas" }
          ],
          a: "completed",
          successTip: "Ancient = very old (centuries!). Derelict = abandoned AND falling apart. Deserted = empty of people (not necessarily old). Three very different words! 🏚️"
        },

        {
          type: "mc",
          q: "Soren writes: 'The ___ factory had been empty since 1989 — windows broken, walls crumbling.'",
          options: ["derelict", "spacious", "charming"],
          a: "derelict",
          tip: "Derelict = abandonado en ruinas (no longer cared for)."
        },

        {
          type: "input",
          q: "Soren's caption: 'The streets were completely ___ — not a single person in sight.'",
          a: "deserted",
          alts: ["Deserted", "empty", "quiet", "desolate"],
          tip: "Deserted = desierto/vacío (empty of people).",
          successTip: "Deserted ≠ desert! DESERTED (adjective) = abandonado/vacío. DESERT (noun) = desierto. Very easy to confuse — don't! 🏜️"
        },

        {
          type: "mc",
          q: "Soren has found what he calls 'a hidden ___' — beautiful but completely unknown to tourists.",
          options: ["gem", "rock", "stone"],
          a: "gem",
          tip: "A hidden gem = una joya escondida (wonderful but little known).",
          successTip: "'A hidden gem' = joya escondida. One of the most used phrases in travel writing and restaurant reviews! 'This place is a hidden gem.' Now use it! 💎"
        },

        {
          type: "mc",
          q: "Have you ever visited an ___ building — one that is thousands of years old?",
          options: ["ancient", "derelict", "high-rise"],
          a: "ancient",
          tip: "Ancient = antiguo/milenario (very, very old)."
        },

        {
          type: "mc",
          q: "Soren: 'I have never been inside a ___ building — I'm always too nervous about safety.'",
          options: ["derelict", "spacious", "comfortable"],
          a: "derelict",
          tip: "Derelict buildings are structurally unsafe.",
          successTip: "Structurally unsafe = estructuralmente inseguro. A derelict building could 'fall apart' at any moment. You'll see that phrase very soon! ⚠️"
        },

        {
          type: "match",
          q: "Match the remaining travel adjectives:",
          pairs: [
            { left: "high-rise", right: "de gran altura / rascacielos" },
            { left: "remote", right: "remoto/aislado" },
            { left: "vibrant", right: "vibrante/lleno de vida" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "Soren has always preferred low buildings. He has ___ liked high-rise apartments.",
          options: ["never", "ever", "already"],
          a: "never",
          tip: "Never = nunca (up to the present — Present Perfect)."
        },

        {
          type: "input",
          q: "Soren's post: 'Have you ___ visited a truly deserted place?'",
          a: "ever",
          alts: ["Ever"],
          tip: "Have you ever = ¿Alguna vez has...? (Present Perfect question)."
        },

        {
          type: "mc",
          q: "That old factory is a hidden ___ — Soren says it's the best discovery he has ever made.",
          options: ["gem", "find", "treasure"],
          a: "gem",
          tip: "Hidden gem = joya escondida.",
          successTip: "You've now seen AND used 'hidden gem' twice in real context! 'The most beautiful building I've ever seen' = superlative + ever. Key pattern unlocked! 🌟"
        }
      ]
    },

    // ===================================================================
    // SET 3: HAVE YOU EVER...? (B1+)
    // ===================================================================
    {
      id: "m10-u6-set3",
      title: "Set 3: Have You Ever...?",
      context: `*Mumbai, Wednesday — Priya's travel interview*

Priya is interviewing Soren for her travel magazine.
"Have you ever stayed somewhere that completely changed the way you see the world?"
Soren laughs. "Yes — more times than I can count."

*Bombay, miércoles — Entrevista de viajes de Priya*

Priya entrevista a Soren para su revista de viajes.
"¿Alguna vez te has quedado en algún lugar que haya cambiado completamente tu forma de ver el mundo?"
Soren ríe. "Sí, más veces de las que puedo contar."`,

      questions: [
        {
          type: "match",
          q: "Match the question structure:",
          pairs: [
            { left: "Have you ever visited...?", right: "¿Alguna vez has visitado...?" },
            { left: "Has she ever stayed...?", right: "¿Alguna vez se ha quedado...?" },
            { left: "Have they ever seen...?", right: "¿Alguna vez han visto...?" }
          ],
          a: "completed",
          successTip: "Have/Has + subject + EVER + past participle? = ¿Alguna vez...? EVER always goes between the auxiliary and the past participle. Always! ✅"
        },

        {
          type: "mc",
          q: "Priya asks: '___ you ever slept in an ancient building?'",
          options: ["Have", "Has", "Did"],
          a: "Have",
          tip: "Have you ever = ¿Alguna vez has...? (Present Perfect)."
        },

        {
          type: "mc",
          q: "Soren replies: 'Yes! I ___ stayed in a 12th-century castle in Scotland.'",
          options: ["have", "had", "did"],
          a: "have",
          tip: "Yes, I have = Present Perfect short answer.",
          successTip: "Short answers! 'Have you ever...?' → 'Yes, I have.' / 'No, I haven't.' NEVER 'Yes, I did.' ❌ Keep the same auxiliary! 💬"
        },

        {
          type: "input",
          q: "Priya writes: '___ Soren ever visited a truly off the beaten track destination?'",
          a: "Has",
          alts: ["has"],
          tip: "Has (he/she/it) ever = ¿Alguna vez ha...? (third person singular)."
        },

        {
          type: "mc",
          q: "Priya: 'Has this trip ___ blown your mind in any way?'",
          options: ["ever", "never", "already"],
          a: "ever",
          tip: "Ever in questions = alguna vez.",
          successTip: "'Blow your mind' = alucinarte / dejarte sin palabras. 'That building blew my mind!' Very enthusiastic informal expression. You'll use it next! 🤯"
        },

        {
          type: "mc",
          q: "Soren: 'The derelict factory in Porto ___ my mind the moment I walked in.'",
          options: ["blew", "blown", "was blowing"],
          a: "blew",
          tip: "Blow → blew (Past Simple, single completed event).",
          successTip: "You used 'blow your mind'! Blow → blew → blown. 'It blew my mind' = me voló la cabeza / me dejó sin palabras. Very natural in spoken English! 🤯"
        },

        {
          type: "mc",
          q: "Have you ever ___ a more charming street than this one?",
          options: ["seen", "saw", "see"],
          a: "seen",
          tip: "See → seen (past participle for Present Perfect)."
        },

        {
          type: "order",
          q: "Traduce: '¿Alguna vez te has quedado en un edificio abandonado?'",
          words: ["Have", "you", "ever", "stayed", "in", "a", "derelict", "building?"],
          a: "Have you ever stayed in a derelict building?",
          extra: ["Did", "stay", "ever"]
        },

        {
          type: "mc",
          q: "Priya: '___ any building ever made you feel truly at home?'",
          options: ["Has", "Have", "Did"],
          a: "Has",
          tip: "Has + any building (singular) + ever = Present Perfect question."
        },

        {
          type: "input",
          q: "Soren's answer: 'Yes — a tiny cottage in Iceland ___ completely blown my mind.'",
          a: "has",
          alts: ["Has"],
          successTip: "A cottage (singular) = has. Third person singular! 'It has blown my mind.' You've now seen, practised, and fully used 'blow your mind'! 🔑"
        }
      ]
    },

    // ===================================================================
    // SET 4: I'VE NEVER... (B2-)
    // ===================================================================
    {
      id: "m10-u6-set4",
      title: "Set 4: Things I've Never Done",
      context: `*Istanbul, Wednesday evening — Amara arrives*

Amara has just landed in Istanbul for the first time.
She stands in front of the Hagia Sophia, speechless.
"I have never seen anything like this in my life."
"I've never felt so small next to a building."

*Estambul, miércoles por la tarde — Amara llega*

Amara acaba de aterrizar en Estambul por primera vez.
Se para frente a la Hagia Sophia, sin palabras.
"Nunca he visto nada igual en mi vida."
"Nunca me he sentido tan pequeña junto a un edificio."`,

      questions: [
        {
          type: "match",
          q: "Match the 'never' structures:",
          pairs: [
            { left: "I've never seen", right: "Nunca he visto" },
            { left: "She's never been", right: "Ella nunca ha estado" },
            { left: "We've never felt", right: "Nunca hemos sentido" }
          ],
          a: "completed",
          successTip: "Never goes BETWEEN have/has and the past participle. 'I have NEVER seen' ✅ NOT 'I never have seen' ❌ Position is everything! 📍"
        },

        {
          type: "mc",
          q: "Amara: 'I ___ never stood in front of something so ancient and overwhelming.'",
          options: ["have", "had", "did"],
          a: "have",
          tip: "I have never = nunca he (Present Perfect, up to now)."
        },

        {
          type: "match",
          q: "Match the travel expressions:",
          pairs: [
            { left: "in the middle of nowhere", right: "en el medio de la nada" },
            { left: "breathtaking", right: "impresionante / que quita el aliento" },
            { left: "bustling", right: "bullicioso / lleno de actividad" }
          ],
          a: "completed",
          successTip: "'In the middle of nowhere' = completamente aislado. 'The hostel was in the middle of nowhere — no shops, no people!' You'll use this very soon! 📍"
        },

        {
          type: "mc",
          q: "Amara: 'I have ___ visited such a ___ city — Istanbul has energy everywhere.'",
          options: ["never / bustling", "ever / derelict", "never / deserted"],
          a: "never / bustling",
          tip: "Never = nunca. Bustling = bullicioso, lleno de vida.",
        successTip: "bustling = animado, concurrido, lleno de vida, crowded = lleno, abarrotado, demasiado concurrido"
        },

        {
          type: "mc",
          q: "Soren texts Amara: 'Have you found anything ___ yet — far from the tourist centres?'",
          options: ["in the middle of nowhere", "in the middle of something", "off the high street"],
          a: "in the middle of nowhere",
          tip: "In the middle of nowhere = completamente aislado / en el medio de la nada.",
          successTip: "You used it! British slang version: 'out in the sticks' = same meaning! 🌾"
        },

        {
          type: "mc",
          q: "Amara replies: 'I ___ never been to a place like Istanbul — it blows my mind.'",
          options: ["have", "has", "had"],
          a: "have",
          tip: "I have never = I + have + never + past participle."
        },

        {
          type: "input",
          q: "Amara's post: 'I had never ___ such a breathtaking view from a rooftop before.'",
          a: "seen",
          alts: ["Seen","witnessed", "experienced", "enjoyed", "beheld"],
          tip: "See → seen (Present Perfect past participle)."
        },

        {
          type: "order",
          q: "Traduce: 'Nunca habia visitado un lugar tan antiguo y espacioso.'",
          words: ["I", "had", "never", "visited", "such", "an", "ancient", "and", "spacious", "place."],
          a: "I had never visited such an ancient and spacious place.",
          extra: ["have", "visit", "been"]
        },

        {
          type: "mc",
          q: "Amara's caption: 'Has ___ ever felt truly speechless in front of a building?'",
          options: ["anyone", "someone", "no one"],
          a: "anyone",
          tip: "Anyone in open questions = cualquier persona. 'Has anyone ever felt...?, 'Someone = alguien específico que ya tienes en mente.",
          successTip: "'Speechless' = sin palabras. Idiom: 'lost for words' = exactamente lo mismo. 'She was completely lost for words.' Very natural in English! 🤐"
        },

        {
          type: "mc",
          q: "Amara concludes: 'I have ___ experienced anything quite like this city.'",
          options: ["never", "ever", "already"],
          a: "never",
          tip: "Never = nunca (negative Present Perfect experience up to now)."
        }
      ]
    },
       // ===================================================================
    // SET 5: SUPERLATIVES + EVER (B2-)
    // ===================================================================
    {
      id: "m10-u6-set5",
      title: "Set 5: The Most... I've Ever...",
      context: `*Istanbul, Thursday morning — Soren and Priya explore together this vibrant city*

Soren and Priya meet Amara for breakfast.
"This is the most spacious café I've ever sat in," says Priya.
"And the most charming," adds Soren.
Each has a story — a first, a superlative, a never-before moment.

*Estambul, jueves por la mañana — Soren y Priya exploran juntos esta vibrante ciudad*

Soren y Priya se encuentran con Amara para el desayuno.
"Este es el café más espacioso en el que jamás me he sentado," dice Priya.
"Y el más encantador," agrega Soren.
Cada uno tiene una historia — una primera vez, un superlativo, un momento único.`,

      questions: [
        {
          type: "match",
          q: "Match the superlative + ever structures:",
          pairs: [
           { left: "the most spacious place", right: "I've ever lived in" },
  { left: "the most charming street", right: "I've ever walked down" },
  { left: "the tallest building", right: "I've ever looked up at" }
          ],
          a: "completed",
          successTip: "Superlative + ever = the ultimate experience! 'The most beautiful place I've ever seen.' This structure = instantly powerful writing! 💫"
        },

        {
          type: "mc",
          q: "Priya: 'By the time I left Istambul. It was the most ancient neighbourhood I ___ ever ___ in.'",
          options: ["have / walked", "had / walked", "have / walk"],
          a: "have / walked",
          tip: "Present all-time superlative = have ever + past participle."
        },

        {
          type: "mc",
          q: "Soren: 'That hostel was the most ___ place I've ever stayed in. I loved it!'",
          options: ["comfortable", "ancient", "deserted"],
          a: "comfortable",
          tip: "Comfortable = best adjective for a positive hostel experience.",
          successTip: "Hostel vs Hotel! HOSTEL = shared dormitory, cheap, social. HOTEL = private room, more expensive. 'The most comfortable hostel I've ever stayed in.' ✅ 🛏️"
        },

        {
          type: "input",
          q: "Priya's column: 'Istanbul is the most ___ city I have ever visited.😁'",
          a: "vibrant",
          alts: ["Vibrant","fascinating", "beautiful", "historic", "cosmopolitan", "chaotic", "lively", "dynamic", "diverse", "enchanting", "unforgettable", "intriguing"],
          tip: "Vibrant = vibrante (full of life and colour)."
        },

        {
          type: "match",
          q: "Match the phrasal verbs for building conditions:",
          pairs: [
            { left: "fall apart", right: "desmoronarse / venirse abajo" },
            { left: "hold up", right: "aguantar / mantenerse en pie" },
            { left: "wear down", right: "desgastarse con el tiempo" }
          ],
          a: "completed",
          successTip: "'Fall apart' = desmoronarse (literally OR figuratively). 'The derelict building was falling apart.' Also: 'He fell apart after the bad news.' Same phrasal, two worlds! 😢"
        },
        {
          type: "mc",
          q: "Soren: 'That old bridge looked like it could ___ at any moment — but it was beautiful.'",
          options: ["fall apart", "fall down", "fall off"],
          a: "fall apart",
          tip: "Fall apart = desmoronarse (lose structure completely)."
        },

        {
          type: "mc",
          q: "Priya's headline: 'The most ___ building I've ever seen — an abandoned factory reborn.'",
          options: ["derelict", "spacious", "popular"],
          a: "derelict",
          tip: "Derelict = abandoned and falling apart.",
          successTip: "Reborn = renacido. 'A derelict building reborn as an art space.' Very cinematic writing. One word transforms the sentence! 🎬"
        },

        {
          type: "order",
          q: "Traduce: 'Es el lugar más encantador en el que jamás me he hospedado.'",
          words: ["It", "is", "the", "most", "charming", "place", "I", "have", "ever", "stayed", "in."],
          a: "It is the most charming place I have ever stayed in.",
          extra: ["had", "stay", "horrible"]
        },

        {
          type: "mc",
          q: "Amara: 'That high-rise was the tallest building I ___ ever ___ into.'",
          options: ["have / walked", "had / walked", "have / walk"],
          a: "have / walked",
          tip: "All-time superlative experience = have ever + past participle."
        },

        {
          type: "input",
          q: "Priya's final line: 'Istanbul is the most breathtaking city I ___ ___ visited.'",
          a: "have ever",
          alts: ["Have ever", "had ever"],
          successTip: "Superlative + have ever = the gold standard of travel writing! 'The most...I have ever...' — master this and your English instantly sounds more sophisticated! 🌍"
        }
      ]
    },

    // ===================================================================
    // SET 6: HAD EVER / HAD NEVER (B2-)
    // ===================================================================
    {
      id: "m10-u6-set6",
      title: "Set 6: Nothing Had Prepared Me",
      context: `*Rural Morocco — Amara's journal, two years earlier*

Before Istanbul, Amara had travelled to Morocco alone.
She arrived in a village so remote it had no electricity.
"It was the most deserted place I had ever visited."
"Nothing I had ever seen before had prepared me for it."

*Marruecos rural — Diario de Amara, dos años antes*

Antes de Estambul, Amara había viajado sola a Marruecos.
Llegó a un pueblo tan remoto que no tenía electricidad.
"Era el lugar más desierto que jamás había visitado."
"Nada de lo que había visto antes me había preparado para eso."`,

      questions: [
        {
          type: "match",
          q: "Match the Past Perfect + ever/never structures:",
          pairs: [
            { left: "It was the most...I had ever seen.", right: "El más...que había visto jamás." },
            { left: "She had never felt so alone.", right: "Nunca había sentido tanta soledad." },
            { left: "Nothing had ever prepared her.", right: "Nada la había preparado jamás." }
          ],
          a: "completed",
          successTip: "Had ever/never = looking FURTHER back in time. 'It was the best I had ever seen' = comparing to everything before that past moment! ⏪"
        },

        {
          type: "mc",
          q: "Amara: 'It was the most remote village I ___ ever ___ .'",
          options: ["had / visited", "has / visited", "had / visit"],
          a: "had / visited",
          tip: "Past superlative experience = had ever + past participle."
        },

        {
          type: "mc",
          q: "She ___ never stayed somewhere so far off the beaten track before that trip.",
          options: ["had", "have", "has"],
          a: "had",
          tip: "Had never = nunca había (before that past moment).",
          successTip: "Had never vs Have never! HAD NEVER = up to a point in the past. HAVE NEVER = up to now. Timeline is everything! ⏰"
        },

        {
          type: "match",
          q: "Match the phrasal verbs for reaction:",
          pairs: [
            { left: "grow on you", right: "gustarte más con el tiempo" },
            { left: "take your breath away", right: "quitarte el aliento / impresionarte" },
            { left: "sink in", right: "asimilarse / hacerse real" }
          ],
          a: "completed",
          successTip: "'Grow on you' = empezar a gustarte más con el tiempo. 'At first I hated the city, but it grew on me.' Very natural! You'll use this next! 🌱"
        },

        {
          type: "mc",
          q: "Amara's journal: 'The silence had ___ my breath away — I had never experienced anything like it.'",
          options: ["taken", "took", "taking"],
          a: "taken",
          tip: "Take → taken. Had taken my breath away = Past Perfect.",
          successTip: "You used 'take your breath away'! = quitarte el aliento (impresionarte). 'The view took my breath away.' Essential travel vocabulary! 😮"
        },

        {
          type: "mc",
          q: "At first the village felt strange. But slowly, it ___ on Amara.",
          options: ["grew", "grown", "growing"],
          a: "grew",
          tip: "Grow → grew (Past Simple of grow on you).",
          successTip: "You used 'grew on her'! = le fue gustando más. 'It grew on me' is always Past Simple — it describes a gradual completed process! 🌿"
        },

        {
          type: "mc",
          q: "It was only after a few days that the beauty of the place ___ in.",
          options: ["sank", "sunk", "sinked"],
          a: "sank",
          tip: "Sink → sank (Past Simple, irregular).",
          successTip: "'It sank in' = se asimiló / se hizo real. 'When the news finally sank in, she cried.' Used for delayed emotional realisations! 💡"
        },

        {
          type: "order",
          q: "Traduce: 'Era el lugar más abandonado que jamás había visto.'",
          words: ["It", "was", "the", "most", "derelict", "place", "she", "had", "ever", "seen."],
          a: "It was the most derelict place she had ever seen.",
          extra: ["have", "saw", "seen"],
          successTip: "an abandoned house = una casa abandonada; a derelict house = una casa abandonada y en mal estado"
        },

        {
          type: "mc",
          q: "Amara wrote: 'I ___ never been in the middle of nowhere quite like that before.'",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Had never been = nunca había estado (before that point)."
        },

        {
          type: "input",
          q: "Journal close: 'Nothing I ___ ___ seen before had prepared me for how beautiful it was.'",
          a: "had ever",
          alts: ["Had ever"],
          successTip: "'Nothing I had ever seen' = nada de lo que había visto jamás. Powerful negative + ever + Past Perfect. Elite storytelling structure! ✍️"
        }
      ]
    },

    // ===================================================================
    // SET 7: SUPERLATIVES + HAD EVER (B2)
    // ===================================================================
    {
      id: "m10-u6-set7",
      title: "Set 7: Priya's Travel Column",
      context: `*New Delhi — Priya's weekly column*

Priya writes about the buildings that have defined her travels.
"Architecture tells stories that words sometimes can't."
She uses superlatives to capture moments that had stopped her in her tracks.

*Nueva Delhi — Columna semanal de Priya*

Priya escribe sobre los edificios que han definido sus viajes.
"La arquitectura cuenta historias que las palabras a veces no pueden."
Usa superlativos para capturar momentos que la habían dejado paralizada.`,

      questions: [
        {
          type: "match",
          q: "Match the journalistic expressions:",
          pairs: [
            { left: "stop someone in their tracks", right: "dejar a alguien paralizado / sin poder avanzar" },
            { left: "leave a mark", right: "dejar huella" },
            { left: "stand the test of time", right: "resistir el paso del tiempo" }
          ],
          a: "completed",
          successTip: "'Stop in your tracks' = quedarte paralizado de sorpresa. 'The view stopped me in my tracks.' Very dramatic — perfect for travel writing! 🛑"
        },

        {
          type: "mc",
          q: "Priya writes: 'The Hagia Sophia was the most ancient building I ___ ever ___ inside.'",
          options: ["had / been", "have / been", "had / be"],
          a: "had / been",
          tip: "Past superlative up to that point = had ever + past participle."
        },

        {
          type: "mc",
          q: "That view ___ her in her tracks — she had never seen anything so breathtaking.",
          options: ["stopped", "had stopped", "was stopping"],
          a: "stopped",
          tip: "Stopped = Past Simple (sudden single moment).",
          successTip: "You used 'stopped in her tracks'! ✅ Stopped (Past Simple) = the sudden moment. 'The view stopped her in her tracks.' Powerful and precise! 🎯"
        },

        {
          type: "input",
          q: "Priya's column: 'It was the most ___ neighbourhood I had ever walked through.' (encantador)",
          a: "charming",
          alts: ["Charming"],
          tip: "Charming = encantador (attractive, pleasant character)."
        },

        {
          type: "mc",
          q: "Priya: 'That open-plan library was the most ___ public space I had ever entered.'",
          options: ["spacious", "derelict", "deserted"],
          a: "spacious",
          tip: "Spacious = the best fit for an open-plan library.",
          successTip: "Open-plan + spacious = natural collocation! 'An open-plan, spacious workspace.' These two adjectives are best friends in design writing! 🏛️"
        },

        {
          type: "mc",
          q: "The ancient ruins ___ a mark on Priya that she had never expected.",
          options: ["left", "had left", "were leaving"],
          a: "left",
          tip: "Left a mark = Past Simple (completed impact).",
          successTip: "You used 'left a mark'! = dejó huella. 'That trip left a mark on me.' Also: 'make a mark' = destacar en algo. Two different expressions! 🖊️"
        },

        {
          type: "mc",
          q: "Priya: 'I had never ___ in a high-rise before that Tokyo apartment — it took my breath away.'",
          options: ["lived", "live", "living"],
          a: "lived",
          tip: "Had never lived = nunca había vivido (before that experience)."
        },

        {
          type: "order",
          q: "Traduce: 'Era la calle más encantadora por la que jamás había caminado.'",
          words: ["It", "was", "the", "most", "charming", "street", "she", "had", "ever", "walked", "down."],
          a: "It was the most charming street she had ever walked down.",
          extra: ["have", "walked", "through"]
        },

        {
          type: "mc",
          q: "Priya concludes: 'Great buildings ___ the test of time — and great travel writing should too.'",
          options: ["stand", "stood", "has stood"],
          a: "stand",
          tip: "Stand the test of time = present truth/fact = Present Simple.",
          successTip: "You used 'stand the test of time'! = resistir el paso del tiempo. Also used for music, art, relationships! 'Their friendship has stood the test of time.' ⏳"
        },

        {
          type: "input",
          q: "Column closing line: 'It was quite simply the most ___ place I had ever set foot in.' (espacioso)",
          a: "spacious",
          alts: ["Spacious"],
          successTip: "'Set foot in' = poner un pie en / visitar por primera vez. 'The most beautiful place I had ever set foot in.' More elegant than 'visited'! 👣"
        }
      ]
    },

    // ===================================================================
    // SET 8: EVER IN QUESTIONS & NEGATIVE SURPRISES (B2)
    // ===================================================================
    {
      id: "m10-u6-set8",
      title: "Set 8: Questions and Surprises",
      context: `*Istanbul — Amara and Soren on a building tour*

Amara takes Soren on a tour of Istanbul's lesser-known buildings.
"Have you ever been somewhere that felt completely frozen in time?"
Soren shakes his head. "Never. Until now."

*Estambul — Amara y Soren en un recorrido de edificios*

Amara lleva a Soren a un recorrido por edificios menos conocidos de Estambul.
"¿Alguna vez has estado en un lugar que se sintiera completamente congelado en el tiempo?"
Soren niega con la cabeza. "Nunca. Hasta ahora."`,

      questions: [
        {
          type: "match",
          q: "Match the expressions for disbelief:",
          pairs: [
            { left: "frozen in time", right: "congelado en el tiempo" },
            { left: "out of this world", right: "fuera de lo común / increíble" },
            { left: "like stepping back in time", right: "como retroceder en el tiempo" }
          ],
          a: "completed",
          successTip: "'Out of this world' = fuera de este mundo / increíble. 'The architecture was out of this world!' Informal superlative — stronger than 'amazing'! 🌍✨"
        },

        {
          type: "mc",
          q: "Amara: '___ you ever been somewhere that felt frozen in time?'",
          options: ["Have", "Has", "Did"],
          a: "Have",
          tip: "Have you ever = ¿Alguna vez has...?"
        },

        {
          type: "mc",
          q: "Soren: 'This district is ___ this world — I have never seen anything like it.'",
          options: ["out of", "outside", "beyond"],
          a: "out of",
          tip: "Out of this world = increíble, fuera de lo común.",
          successTip: "You used 'out of this world'! ✅ Common responses: 'The food was out of this world!' Very enthusiastic compliment — use it freely! 🤩"
        },

        {
          type: "mc",
          q: "Amara: 'Walking in here is ___ stepping back in time — it hasn't changed in centuries.'",
          options: ["like", "as", "similar"],
          a: "like",
          tip: "Like + verb-ing = como si estuvieras haciendo algo.",
          successTip: "'Like stepping back in time' = como retroceder en el tiempo. 'Visiting Pompeii is like stepping back in time.' Perfect for ancient places! ⏳"
        },

        {
          type: "mc",
          q: "'___ this building ever been used as anything other than a mosque?'",
          options: ["Has", "Have", "Was"],
          a: "Has",
          tip: "Has + singular subject + ever = Present Perfect question."
        },

        {
          type: "input",
          q: "Soren's blog: 'I never ___ it would be so breathtaking — I was completely lost for words.' (expect → Past Simple)",
          a: "expected",
          alts: ["thought", "imagined", "believed", "knew"],
          tip: "Never expected = nunca esperé (Past Simple, completed thought).",
          successTip: "'Lost for words' = sin palabras. You saw this in Set 4! Now using it in a new context. 'I was completely lost for words.' Natural and expressive! 🤐"
        },

        {
          type: "mc",
          q: "Amara: 'Has anyone ___ told you that this neighbourhood is a hidden gem?'",
          options: ["ever", "never", "already"],
          a: "ever",
          tip: "Has anyone ever = ¿Alguien alguna vez ha...?"
        },

        {
          type: "order",
          q: "Traduce: '¿Alguna vez has visitado un edificio que se sintiera completamente fuera de este mundo?'",
          words: ["Have", "you", "ever", "visited", "a", "building", "that", "felt", "completely", "out", "of", "this", "world?"],
          a: "Have you ever visited a building that felt completely out of this world?",
          extra: ["Did", "feel", "been"]
        },

        {
          type: "mc",
          q: "Soren: '___ this area always been so deserted, or did it used to be more popular?'",
          options: ["Has", "Have", "Was"],
          a: "Has",
          tip: "Has + this area (singular) + always been = Present Perfect."
        },

        {
          type: "input",
          q: "Amara's final question: 'Has any building ___ truly changed the way you see architecture?'",
          a: "ever",
          alts: ["Ever"],
          successTip: "Has any building ever...? = perfect reflective question. Ask this in any conversation about art, travel or architecture and you'll sound wonderfully sophisticated! 🏛️"
        }
      ]
    },

    // ===================================================================
    // SET 9: IDIOMS, PHRASAL VERBS & TRAVEL STORIES (B2+)
    // ===================================================================
    {
      id: "m10-u6-set9",
      title: "Set 9: The Language of Travel",
      context: `*London — Travel writing awards ceremony*

All four — Amara, Soren, Priya, and Yuki — are at a travel writing award.
Each has been shortlisted for their writing about buildings and first times.
"None of us had ever imagined ending up in a room like this."

*Londres — Ceremonia de premios de escritura de viajes*

Los cuatro — Amara, Soren, Priya y Yuki — están en un premio de escritura de viajes.
Cada uno ha sido preseleccionado por su escritura sobre edificios y primeras veces.
"Ninguno de nosotros había imaginado terminar en una sala como esta."`,

      questions: [
        {
          type: "match",
          q: "Match these idioms — you'll use all of them this set:",
          pairs: [
            { left: "put something on the map", right: "dar a conocer algo / hacerlo famoso" },
            { left: "off the radar", right: "desconocido / sin atención pública" },
            { left: "a world away from", right: "completamente diferente a" }
          ],
          a: "completed",
          successTip: "'Put on the map' = hacer famoso un lugar. 'That article put the village on the map.' Journalists literally do this! ✍️🗺️"
        },

        {
          type: "mc",
          q: "Priya's shortlisted article was about a village that had been completely ___ the radar for decades.",
          options: ["off", "under", "below"],
          a: "off",
          tip: "Off the radar = desconocido / sin atención pública.",
          successTip: "You used 'off the radar'! ✅ The opposite: 'on everyone's radar' = en boca de todos / muy conocido. 'That restaurant is on everyone's radar right now!' 📡"
        },

        {
          type: "mc",
          q: "Soren's piece had ___ a tiny Moroccan town ___ the map.",
          options: ["put / on", "taken / on", "brought / to"],
          a: "put / on",
          tip: "Put something on the map = dar a conocer un lugar.",
          successTip: "You used 'put on the map'! ✅ Also: 'wipe off the map' = destruir completamente. 'The earthquake wiped the village off the map.' Opposite, dramatic! 💥"
        },

        {
          type: "mc",
          q: "Amara: 'That rural village was ___ anything I had ever experienced in Lagos.'",
          options: ["a world away from", "a long way off", "a far cry to"],
          a: "a world away from",
          tip: "A world away from = completamente diferente a.",
          successTip: "'A world away from' = a la misma vez literario y coloquial. 'The quiet countryside was a world away from the city noise.' Elegant contrast! 🌍"
        },

        {
          type: "mc",
          q: "Yuki: 'Has winning this award ___ sunk in yet?'",
          options: ["fully", "full", "fuller"],
          a: "fully",
          tip: "Fully sunk in = completely processed/realised.",
          successTip: "'Has it sunk in yet?' = ¿Ya lo has asimilado? You saw 'sink in' in Set 6! Now it appears in a completely different context. Vocabulary recycled! 💡"
        },

        {
          type: "mc",
          q: "Soren laughs: 'Honestly? Not yet. This is the most surreal evening I ___ ever ___.'",
          options: ["have / experienced", "had / experienced", "have / experience"],
          a: "have / experienced",
          tip: "All-time superlative up to now = have ever + past participle."
        },

        {
          type: "order",
          q: "Traduce: 'Ninguno de nosotros había imaginado jamás terminar aquí.'",
          words: ["None", "of", "us", "had", "ever", "imagined", "ending", "up", "here."],
          a: "None of us had ever imagined ending up here.",
        },

        {
          type: "mc",
          q: "Priya's acceptance speech: 'I have never ___ so proud to call myself a travel writer.'",
          options: ["felt", "feel", "feeling"],
          a: "felt",
          tip: "Feel → felt (Past Perfect/Perfect participle)."
        },

        {
          type: "mc",
          q: "Amara: 'Every building I have ever entered has ___ a mark on me in some way.'",
          options: ["left", "left behind", "made"],
          a: "left",
          tip: "Left a mark = dejado huella.",
          successTip: "You've now seen 'left a mark' in Sets 7 AND 9. Perfect vocabulary recycling! This is how real language learning works — meeting words in new contexts! 🔄"
        },

        {
          type: "input",
          q: "The host announces: 'None of our winners had ___  written about places quite like this before.'",
          a: "ever",
          alts: ["Ever"],
          successTip: "'Had ever written' = había escrito jamás. Past Perfect + ever = the ultimate compliment for original writing. You've mastered the full ever/never toolkit! 🏆"
        }
      ]
    },

    // ===================================================================
    // SET 10: FULL CONTRAST — PRESENT & PAST PERFECT + EVER/NEVER (B2+)
    // ===================================================================
    {
      id: "m10-u6-set10",
      title: "Set 10: The Complete Picture",
      context: `*One year later — all four are writing their memoirs*

Each writer reflects on the places that have shaped them.
"The buildings we enter change us," writes Priya.
"Some you never forget. Some you wish you had found sooner."

*Un año después — los cuatro escriben sus memorias*

Cada escritor reflexiona sobre los lugares que les han marcado.
"Los edificios en los que entramos nos cambian," escribe Priya.
"A algunos nunca los olvidas. Ojalá hubiera encontrado algunos antes."`,

      questions: [
        {
          type: "match",
          q: "Match each structure to its meaning:",
          pairs: [
            { left: "I have never seen (present perfect)", right: "Nunca he visto — up to now" },
            { left: "I had never seen (past perfect)", right: "Nunca había visto — up to that past moment" },
            { left: "The best I have ever seen", right: "El mejor que he visto jamás — all time up to now" }
          ],
          a: "completed",
          successTip: "The full picture! Present Perfect = life up to NOW. Past Perfect = life up to a PAST MOMENT. Same structure, different time anchors! ⚓"
        },

        {
          type: "mc",
          q: "Priya now: 'I ___ never visited a city as vibrant as Istanbul.' (all-time truth)",
          options: ["have", "had", "was"],
          a: "have",
          tip: "All-time truth up to now = Present Perfect."
        },

        {
          type: "mc",
          q: "Priya then: 'When I arrived in Istanbul, I ___ never seen anything like it.' (up to that past moment)",
          options: ["had", "have", "was"],
          a: "had",
          tip: "Up to a past moment = Past Perfect.",
          successTip: "Compare! 'I have never seen' (still true now) vs 'I had never seen' (true up to that past trip). Same words, very different timelines! ⏰"
        },

        {
          type: "mc",
          q: "Soren's memoir: 'That derelict factory was the most beautiful place I ___ ever ___ foot in.'",
          options: ["had / set", "have / set", "had / put"],
          a: "had / set",
          tip: "Past superlative = had ever + past participle."
        },

        {
          type: "mc",
          q: "Yuki writes: 'London is the most ___ city I have ever lived in — always something new.'",
          options: ["vibrant", "derelict", "deserted"],
          a: "vibrant",
          tip: "Vibrant = full of life (best fit for a busy city like London)."
        },

        {
          type: "order",
          q: "Traduce: 'Era el edificio más espacioso que jamás había diseñado, y el más encantador.'",
          words: ["It", "was", "the", "most", "spacious", "building", "she", "had", "ever", "designed,", "and", "the", "most", "charming."],
          a: "It was the most spacious building she had ever designed, and the most charming.",
          extra: ["designed", "have", "charming"]
        },

        {
          type: "mc",
          q: "Carlos sends a message: '___ any of you ever visited a place that completely put your hometown on the map?'",
          options: ["Has", "Have", "Had"],
          a: "Has",
          tip: "Has any of you = formal Present Perfect question (singular).",
          successTip: "'Put your hometown on the map' — you've recycled this from Set 9! Using idioms across different contexts = real fluency. You're doing it! 🗺️✅"
        },

        {
  type: "mc",
  q: "Amara's closing line: 'I have ___ regretted choosing architecture.'",
  options: ["never", "ever", "always"],
  a: "never",
  tip: "Have never = nunca he (present truth up to now)."
},

        {
          type: "mc",
          q: "Priya's final sentence: 'The most important place I have ever visited is the one that ___ my world upside down.'",
          options: ["turned", "had turned", "was turning"],
          a: "turned",
          tip: "Turned = Past Simple (completed single event).",
          successTip: "'Turn your world upside down' = cambiar tu mundo por completo. 'That trip turned my world upside down.' One of the most vivid idioms in travel English! 🌍🔄"
        },

        {
          type: "input",
          q: "Group memoir title: 'Places We ___ Never Forgotten — and People We ___ never Expected to Meet.'",
          a: "Have / have",
          alts: ["have have"],
          successTip: "🏆 UNIT 6 COMPLETE! Ever/Never with Present AND Past Perfect ✅ Building vocabulary ✅ 15+ idioms and phrasal verbs used in context ✅ You write like a travel journalist! ✈️🌍"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u6-reading",
    title: "Reading: First Times — Four Stories",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**Amara — The Village With No Electricity**

Amara had never been anywhere so remote. The village had no electricity, no signal, and no tourists. It was the most deserted place she had ever visited. At first, the silence felt strange. But slowly, it grew on her.`
      },
      {
        type: "mc",
        q: "Why was the village unusual?",
        options: ["No electricity, signal or tourists", "No food or water", "No roads or paths"],
        a: "No electricity, signal or tourists",
        tip: "'No electricity, no signal, and no tourists.'"
      },
      {
        type: "mc",
        q: "How did Amara feel about the silence at first?",
        options: ["Strange", "Relaxing", "Exciting"],
        a: "Strange",
        tip: "'At first, the silence felt strange.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**Amara — What Happened Next**

After three days, the beauty of the place finally sank in. Amara had never felt so at peace. She later wrote that it was a world away from Lagos — and that nothing she had ever seen before had prepared her for it.`
      },
      {
        type: "mc",
        q: "How long did it take for the beauty to 'sink in'?",
        options: ["Three days", "One week", "One day"],
        a: "Three days",
        tip: "'After three days, the beauty finally sank in.'"
      },
      {
        type: "mc",
        q: "How did Amara describe the village compared to Lagos?",
        options: ["A world away from it", "Very similar to it", "More vibrant than it"],
        a: "A world away from it",
        tip: "'It was a world away from Lagos.'"
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Soren — The Derelict Factory**

Soren had been looking for hidden gems across Europe when he found the abandoned factory in Porto. It had been derelict for thirty years. The moment he walked in, it blew his mind. He had never set foot in anywhere quite like it.`
      },
      {
        type: "mc",
        q: "How long had the factory been derelict?",
        options: ["Thirty years", "Ten years", "Fifty years"],
        a: "Thirty years",
        tip: "'It had been derelict for thirty years.'"
      },
      {
        type: "mc",
        q: "What was Soren looking for when he found the factory?",
        options: ["Hidden gems across Europe", "Ancient buildings in Portugal", "Popular tourist sites"],
        a: "Hidden gems across Europe",
        tip: "'...looking for hidden gems across Europe.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Priya — The High-Rise Surprise**

Priya had always preferred ancient, charming buildings to modern ones. She had never lived in a high-rise before her Tokyo apartment. But the view from the 42nd floor took her breath away. It was, she admitted, the most spacious apartment she had ever seen.`
      },
      {
        type: "mc",
        q: "What type of buildings had Priya always preferred?",
        options: ["Ancient and charming ones", "Modern high-rises", "Open-plan offices"],
        a: "Ancient and charming ones",
        tip: "'...always preferred ancient, charming buildings.'"
      },
      {
        type: "mc",
        q: "What changed her opinion of the high-rise?",
        options: ["The view from the 42nd floor", "The spacious lobby", "The charming neighbourhood"],
        a: "The view from the 42nd floor",
        tip: "'The view from the 42nd floor took her breath away.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**The Awards Night — Four Writers, One Room**

None of them had ever imagined sitting in the same room together. Soren had never expected to win. Priya had never felt so proud. Amara said it was the most out-of-this-world evening she had ever experienced. Yuki simply smiled — she had finally found her place in the world.`
      },
      {
        type: "mc",
        q: "Who had never expected to win?",
        options: ["Soren", "Priya", "Amara"],
        a: "Soren",
        tip: "'Soren had never expected to win.'"
      },
      {
        type: "mc",
        q: "How did Amara describe the evening?",
        options: ["Out of this world", "A world away from normal", "Off the beaten track"],
        a: "Out of this world",
        tip: "'...the most out-of-this-world evening she had ever experienced.'",
        successTip: "All four characters — all four idioms — all in one final passage. Vocabulary recycled and mastered. That's real fluency! 🌟✈️"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u6-review",
    title: "Unit 6 Review: First Times & Buildings",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: Have you ___ stayed in an ancient building?",
        options: ["ever", "never", "already"],
        a: "ever",
        tip: "Have you ever = ¿Alguna vez has...?"
      },
      {
        type: "mc",
        q: "Review: I ___ never visited such a deserted place.",
        options: ["have", "had", "was"],
        a: "have",
        tip: "All-time truth up to now = have never."
      },
      {
        type: "mc",
        q: "Review: It was the most charming street she ___ ever walked down.",
        options: ["had", "have", "has"],
        a: "had",
        tip: "Past superlative experience = had ever."
      },
      {
        type: "mc",
        q: "Review: That view completely ___ my mind — I couldn't speak.",
        options: ["blew", "blown", "blow"],
        a: "blew",
        tip: "Blow → blew (Past Simple). Blow your mind = alucinarte."
      },
      {
        type: "mc",
        q: "Review: The village was ___ ___ ___ anything in the city.",
        options: ["a world away from", "a world far from", "a far world from"],
        a: "a world away from",
        tip: "A world away from = completamente diferente a."
      },
      {
        type: "mc",
        q: "Review: The apartment had an ___ kitchen and living area — no walls between them.",
        options: ["open-plan", "spacious", "ancient"],
        a: "open-plan",
        tip: "Open-plan = no internal dividing walls."
      },
      {
        type: "mc",
        q: "Review: That article ___ the tiny village ___ the map.",
        options: ["put / on", "took / on", "put / in"],
        a: "put / on",
        tip: "Put on the map = dar a conocer un lugar."
      },
      {
        type: "mc",
        q: "Review: She had never ___ anywhere so remote before that trip.",
        options: ["been", "went", "gone"],
        a: "been",
        tip: "Be → been (Past Perfect irregular participle)."
      },
      {
        type: "mc",
        q: "Review: At first it felt strange, but the city slowly ___ on her.",
        options: ["grew", "grown", "grow"],
        a: "grew",
        tip: "Grow on someone → grew on her (Past Simple)."
      },
      {
        type: "mc",
        q: "Review: It was the most ___ place I had ever set foot in — completely empty, silent, ancient.",
        options: ["deserted", "bustling", "popular"],
        a: "deserted",
        tip: "Deserted = empty of people (best fit for the description).",
        successTip: "🎉 UNIT 6 MASTERED! Ever/Never ✅ Present & Past Perfect ✅ Building adjectives ✅ 15+ idioms in context ✅ Ready for Unit 7! 🚀✈️"
      }
    ]
  }
};
